import { dispatch, pathSubscription, subscription } from 'nike-re-framejs';
import { fromJS, Map } from 'immutable';
import onbClient from '../../registration/util/client';
import { handleServerErrors } from '../../registration/util/error';
import { getConfig } from '../../registration/util/config';
import { nuiCtx } from '../../../common/initialization';
import { clientLog } from '../../../common/logging/logs';
import { OnboardingLog } from '../../../common/logging/OnboardingLog';
import { LEVEL } from '../../../common/logging/LEVEL';
import { searchClient } from '../../../common/axios/searchClient';
import { closeDialog, openDialog } from '../../dialogs/dialogs';
import KycFormDialog from '../view/dialog/KycFormDialog';
import KycSuccessDialog from '../view/dialog/KycSuccessDialog';

export const loadForcedActionsIfLogged = db => {
    const account = db.getIn(['account']);
    if (account.getIn(['user', 'contractNumber']) || account.getIn(['userData', 'cisloZmluvy'])) {
        loadForcedActions();
    }
};

export function loadForcedActions() {
    onbClient
        .get('/post/forced-actions')
        .then(forcedActions => dispatch(setForcedActions(forcedActions)))
        .catch(handleServerErrors());
}

export function setAddresses(addresses) {
    return db => db.setIn(['search', 'addresses'], fromJS(addresses));
}

export function searchStreets(text) {
    return () => {
        if (text && text.length > 2) {
            searchClient
                .get('/addresses/', {
                    params: {
                        text,
                        offset: 0,
                        count: 100,
                        queryFields: 'street'
                    }
                })
                .then(addresses => dispatch(setAddresses(addresses.items)))
                .catch(() => dispatch(setAddresses([])));
        } else {
            return setAddresses([]);
        }
    };
}

export function setMunicipalities(municipalities) {
    return db => db.setIn(['search', 'municipalities'], fromJS(municipalities));
}

export function searchMunicipalities(text) {
    return () => {
        if (text && text.length > 2) {
            searchClient
                .get('/municipalities/', {
                    params: {
                        text,
                        offset: 0,
                        count: 100
                    }
                })
                .then(municipalities => dispatch(setMunicipalities(municipalities.municipalities)))
                .catch(() => dispatch(setMunicipalities([])));
        } else {
            return setMunicipalities([]);
        }
    };
}

export function postChange(changeRequestMap, form) {
    return () => {
        const changeRequest = Object.keys(changeRequestMap).reduce((list, key) => {
            list.push({ parameter: key, newValue: changeRequestMap[key] });
            return list;
        }, []);
        onboardingClient(changeRequest, form);
    };
}

export function createKycAnswer(createAnswerKycArray, form) {
    return db => {
        const kycForm = db.getIn(['onboarding', 'kycForm']);
        onbClient
            .post(`/post/kyc-create-answer`, {
                form_id: kycForm.get('form_id'),
                kyc_requested_id: kycForm.get('kyc_requested_id'),
                kyc: createAnswerKycArray
            })
            .then(forcedActions => dispatch(setForcedActions(forcedActions)))
            .catch(error => {
                clientLog(
                    OnboardingLog(
                        'Create KYC answer /post/kyc-create-answer',
                        error?.stack ?? '',
                        {
                            createAnswerKycArray
                        },
                        LEVEL.ERROR
                    )
                );
                throw error;
            })
            .catch(handleServerErrors(form));

        return db;
    };
}

export function createDefaultKycAnswer(createAnswerKycArray, form) {
    return db => {
        const kycForm = db.getIn(['onboarding', 'kycForm']);
        onbClient
            .post(`/post/kyc-create-answer/default`, {
                form_id: kycForm.get('form_id'),
                kyc: createAnswerKycArray
            })
            .then(resp => dispatch(setKycAnswerResponse(resp)))
            .catch(error => {
                clientLog(
                    OnboardingLog(
                        'Create KYC answer /post/kyc-create-answer/default',
                        error?.stack ?? '',
                        {
                            createAnswerKycArray
                        },
                        LEVEL.ERROR
                    )
                );
                throw error;
            })
            .catch(handleServerErrors(form));

        return db;
    };
}

function setKycAnswerResponse(response) {
    closeDialog(KycFormDialog);
    openDialog(KycSuccessDialog);
    return db => db.setIn(['onboarding', 'kycForm', 'response'], response);
}

export function postRequestChange(changeRequest, form) {
    return () => onboardingClient(changeRequest, form);
}

function onboardingClient(changeRequest, form) {
    return onbClient
        .post(`/post/change`, changeRequest)
        .then(forcedActions => dispatch(setForcedActions(forcedActions)))
        .catch(error => {
            clientLog(
                OnboardingLog(
                    'Post /post/change',
                    error?.stack ?? '',
                    {
                        changeRequest
                    },
                    LEVEL.ERROR
                )
            );
            throw error;
        })
        .catch(handleServerErrors(form));
}

export function setForcedActions(forcedActions) {
    closeDialog(KycFormDialog);
    return db => {
        const updateForm = db.getIn(['onboarding', 'updateForm']);
        if (updateForm) {
            openDialog(KycSuccessDialog);
        }
        return [
            db
                .setIn(['onboarding', 'forcedActions'], fromJS(forcedActions))
                .setIn(['account', nuiCtx.platform === 'mobile' ? 'userData' : 'user', 'forcedRequestedKyc'], false),
            db => getConfig().setForcedActions(forcedActions, db)
        ];
    };
}

export const getAddresses = subscription(db => db.getIn(['search', 'addresses']));
export const getMunicipalities = subscription(db => db.getIn(['search', 'municipalities']));

const onboarding = subscription(db => db.get('onboarding', Map()));

export const getForcedActions = subscription(onb => onb.getIn(['forcedActions']), onboarding);
export const getLobbyOverride = subscription(onb => onb.getIn(['lobbyOverride']), onboarding);
export const manualIdentityCheck = subscription(onb => onb.getIn(['forceManualIdentityCheck']), onboarding);
export const cameraEnabled$ = subscription(onb => onb.getIn(['cameraEnabled'], true), onboarding);

export function setLobbyOverride(value) {
    return db =>
        value ? db.setIn(['onboarding', 'lobbyOverride'], value) : db.deleteIn(['onboarding', 'lobbyOverride']);
}

export function setTbIdentityCheck(value) {
    return db =>
        db.setIn(['onboarding', 'forceManualIdentityCheck'], !value).setIn(['onboarding', 'lobbyOverride'], true);
}

export function setNoCamera(value) {
    return db => db.setIn(['onboarding', 'cameraEnabled'], !value);
}

export function setBioAgreement(checked) {
    const changeEntries = [{ parameter: 'BIOMETRY_DATA_APPROVAL', newValue: +checked, currentValue: '' }];
    onbClient
        .post(`/post/change`, changeEntries)
        .then(dispatch(setResponseBioAgreement(checked)))
        .catch(error => {
            clientLog(
                OnboardingLog(
                    'Set BIOMETRY_DATA_APPROVAL',
                    error?.stack ?? '',
                    {
                        checked
                    },
                    LEVEL.ERROR
                )
            );
            throw error;
        })
        .catch(() => dispatch(setTbIdentityCheck(false)));
    return db => db;
}

function setResponseBioAgreement(checked) {
    return db =>
        db.setIn(['account', nuiCtx.platform === 'portal' ? 'user' : 'userData', 'biometryDataApproval'], checked);
}

export function startTBIdentitySession() {
    onbClient
        .post(`/post/tb-identity`)
        .then(response => (window.location.href = response.url))
        .catch(error => {
            clientLog(OnboardingLog('Start tb_identity_session', error?.stack ?? '', {}, LEVEL.ERROR));
            throw error;
        })
        .catch(() => dispatch(setTbIdentityCheck(false)));
    return db => db;
}

export const accountPortal$ = pathSubscription(['account', 'user']);
const accountMobile$ = pathSubscription(['account', 'userData']);
export const account$ = subscription((l1, l2) => l1 || l2, accountPortal$, accountMobile$);
export const biometryDataApproval$ = subscription(acc => acc.getIn(['biometryDataApproval']), account$);
export const forcedActionStatus$ = subscription(acc => acc.getIn(['forcedActionStatus']), account$);
